'use client'

import { useState, useRef, useEffect, useCallback, type ReactNode } from 'react'
import { usePathname } from 'next/navigation'
import { useUser } from '@/contexts/UserContext'
import { useAIContext } from './AIContext'
import { ModuleInfoModal } from '@/components/modules/ModuleInfoModal'
import { useDeferredStartup } from '@/hooks/useDeferredStartup'
import styles from './Maskot.module.css'

const API_BASE = process.env.NEXT_PUBLIC_API_URL || 'https://app.canerozen.com/api'
const CONFIGURED_AI_URL = process.env.NEXT_PUBLIC_AI_URL || ''
const AI_URL = CONFIGURED_AI_URL && !/^https?:\/\/(localhost|127\.0\.0\.1)(?::|\/|$)/i.test(CONFIGURED_AI_URL)
  ? CONFIGURED_AI_URL
  : `${API_BASE}/panel`

function getToken() {
  if (typeof window === 'undefined') return ''
  return localStorage.getItem('auth_token') || ''
}

function getProactiveRequestKey(pathname: string, liveData: unknown) {
  try {
    return pathname + ':' + JSON.stringify(liveData)
  } catch {
    return pathname + ':live-data'
  }
}

interface Message {
  role: 'user' | 'assistant'
  content: string
  source?: string
}

type HistoryMessage = {
  role?: string
  content?: unknown
  source?: string
}

// Sayfa başlığı başlangıçlarına göre çıkar
function guessPageTitle(pathname: string): string {
  const map: Record<string, string> = {
    '/pages/order/view': 'Sipariş Detay',
    '/pages/order/list': 'Sipariş Listesi',
    '/pages/order': 'Siparişler',
    '/pages/product/list': 'Ürün Listesi',
    '/pages/product/multipleBarcode': 'Toplu Barkod',
    '/pages/product': 'Ürünler',
    '/pages/logistic/invoice/error': 'Fatura Hataları',
    '/pages/logistic/invoice': 'Fatura Yönetimi',
    '/pages/logistic': 'Lojistik',
    '/pages/cargo/list': 'Kargo Listesi',
    '/pages/cargo': 'Kargo',
    '/pages/depot': 'Depo Yönetimi',
    '/pages/campaing/coupon': 'Kupon Yönetimi',
    '/pages/campaing': 'Kampanyalar',
    '/pages/report': 'Raporlar',
    '/pages/member': 'Üyeler',
    '/pages/admin/firm': 'Firma Yönetimi',
    '/pages/virtual_market': 'Sanal Market',
  }
  for (const [key, title] of Object.entries(map)) {
    if (pathname.startsWith(key)) return title
  }
  return 'Panel'
}

export default function Maskot() {
  const pathname = usePathname()
  const isEmbedPath = pathname === '/embed' || pathname?.startsWith('/embed/')
  const { user } = useUser()
  const { getLiveData, clearLiveData } = useAIContext()
  const deferredStartupReady = useDeferredStartup(3000)

  const [open, setOpen] = useState(false)
  const [messages, setMessages] = useState<Message[]>([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [streaming, setStreaming] = useState('')
  const [proactiveMsg, setProactiveMsg] = useState<string | null>(null)
  const [proactiveSeen, setProactiveSeen] = useState<string>('')
  const [assistantEnabled, setAssistantEnabled] = useState(true)
  const [moduleInfoId, setModuleInfoId] = useState(0)
  const historyLoadedRef = useRef(false)
  const proactiveRequestKeyRef = useRef('')

  // Drag state
  const [pos, setPos] = useState({ x: 0, y: 0 })
  const dragging = useRef(false)
  const dragOrigin = useRef({ mx: 0, my: 0, px: 0, py: 0 })
  const didDrag = useRef(false)

  const messagesEndRef = useRef<HTMLDivElement>(null)

  // Sayfa değişince live data temizle ve proaktif kontrol et
  useEffect(() => {
    clearLiveData()
    setProactiveMsg(null)
    if (!deferredStartupReady || isEmbedPath) return

    const timer = setTimeout(async () => {
      try {
        const liveData = getLiveData()
        const requestKey = getProactiveRequestKey(pathname || '', liveData)
        if (proactiveRequestKeyRef.current === requestKey) return
        proactiveRequestKeyRef.current = requestKey
        const res = await fetch(`${AI_URL}/ai/proactive`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(getToken() ? { Authorization: `Bearer ${getToken()}` } : {}),
          },
          body: JSON.stringify({ pageUrl: pathname, liveData })
        })
        const data = await res.json()
        if (data.hasMessage && proactiveSeen !== data.message) {
          setProactiveMsg(data.message)
        }
      } catch (_) {}
    }, 2500)

    return () => clearTimeout(timer)
  }, [clearLiveData, deferredStartupReady, getLiveData, isEmbedPath, pathname, proactiveSeen])


  useEffect(() => {
    historyLoadedRef.current = false
    setMessages([])
  }, [user?.id])

  useEffect(() => {
    if (!deferredStartupReady || isEmbedPath) return
    const token = getToken()
    if (!token) return
    let cancelled = false
    fetch(`${AI_URL}/ai/config`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => res.json())
      .then(data => {
        if (!cancelled && data?.success) setAssistantEnabled(data.data?.enabled !== false)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [deferredStartupReady, isEmbedPath, user?.id])

  const loadHistory = useCallback(async () => {
    if (historyLoadedRef.current) return
    const token = getToken()
    if (!token) return
    historyLoadedRef.current = true

    try {
      const res = await fetch(`${AI_URL}/ai/history?limit=40`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      const data = await res.json().catch(() => ({}))
      const history = Array.isArray(data?.data?.messages) ? data.data.messages : []
      if (!res.ok || !data?.success || history.length === 0) return
      setMessages(prev => prev.length ? prev : history.map((message: HistoryMessage) => ({
        role: message.role === 'assistant' ? 'assistant' : 'user',
        content: String(message.content || ''),
        source: message.source,
      })).filter((message: Message) => message.content))
    } catch (_) {}
  }, [])

  useEffect(() => {
    if (!open) return
    void loadHistory()
  }, [open, loadHistory])

  // Auto scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, streaming])

  // Drag handlers
  const onPointerDown = (e: React.PointerEvent) => {
    dragging.current = true
    didDrag.current = false
    dragOrigin.current = { mx: e.clientX, my: e.clientY, px: pos.x, py: pos.y }
    ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
  }

  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragging.current) return
    const dx = e.clientX - dragOrigin.current.mx
    const dy = e.clientY - dragOrigin.current.my
    if (Math.abs(dx) > 3 || Math.abs(dy) > 3) didDrag.current = true
    setPos({ x: dragOrigin.current.px + dx, y: dragOrigin.current.py + dy })
  }

  const onPointerUp = () => {
    dragging.current = false
  }

  const handleAvatarClick = () => {
    if (!didDrag.current) {
      setOpen(o => !o)
      if (proactiveMsg) {
        setMessages([{ role: 'assistant', content: proactiveMsg, source: 'rule' }])
        setProactiveSeen(proactiveMsg)
        setProactiveMsg(null)
      }
    }
    didDrag.current = false
  }

  // Chat gönder
  const sendMessage = useCallback(async (text?: string) => {
    const query = (text ?? input).trim()
    if (!query || loading) return

    const userMsg: Message = { role: 'user', content: query }
    const newMessages = [...messages, userMsg]
    setMessages(newMessages)
    setInput('')
    setLoading(true)
    setStreaming('')

    try {
      const liveData = getLiveData()
      const res = await fetch(`${AI_URL}/ai/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(getToken() ? { Authorization: `Bearer ${getToken()}` } : {}),
        },
        body: JSON.stringify({
          query,
          context: {
            pageUrl: pathname,
            pageTitle: guessPageTitle(pathname),
            liveData,
            userRole: user?.role ?? 'user'
          },
          messages: newMessages.map(m => ({ role: m.role, content: m.content }))
        })
      })

      if (!res.ok || !res.body) throw new Error('AI servisi yanıt vermedi')

      const reader = res.body.getReader()
      const decoder = new TextDecoder()
      let full = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        const lines = decoder.decode(value).split('\n').filter(l => l.startsWith('data: '))
        for (const line of lines) {
          try {
            const parsed = JSON.parse(line.slice(6))
            if (parsed.delta) {
              full += parsed.delta
              setStreaming(full)
            }
            if (parsed.done) {
              setMessages(prev => [...prev, {
                role: 'assistant',
                content: parsed.full || full,
                source: parsed.source ?? 'llm'
              }])
              setStreaming('')
            }
          } catch (_) {}
        }
      }
    } catch {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Şu an yanıt alamıyorum, birazdan tekrar dene.',
      }])
      setStreaming('')
    } finally {
      setLoading(false)
    }
  }, [input, messages, loading, pathname, user, getLiveData])

  const pageTitle = guessPageTitle(pathname)

  const renderMessageContent = (content: string) => {
    const lines = String(content || '').split('\n')
    const nodes: ReactNode[] = []
    let textBuffer: string[] = []

    const flushText = () => {
      if (!textBuffer.length) return
      nodes.push(<span key={`text-${nodes.length}`}>{textBuffer.join('\n')}</span>)
      textBuffer = []
    }

    lines.forEach((line) => {
      const trimmed = line.trim()
      const pageMatch = trimmed.match(/^PAGE_LINK:(\{.*\})$/)
      const moduleMatch = trimmed.match(/^MODULE_INFO_LINK:(\{.*\})$/)

      if (pageMatch) {
        flushText()
        try {
          const parsed = JSON.parse(pageMatch[1])
          const href = String(parsed.href || '')
          const label = String(parsed.label || 'Hızlıca gitmek için tıklayın')
          if (href.startsWith('/')) {
            nodes.push(
              <a key={`page-link-${nodes.length}`} className={styles.actionLink} href={href}>
                <i className="fa-regular fa-arrow-up-right-from-square" />
                {label}
              </a>
            )
          }
        } catch (_) {}
        return
      }

      if (moduleMatch) {
        flushText()
        try {
          const parsed = JSON.parse(moduleMatch[1])
          const moduleId = Number(parsed.moduleId || parsed.module_id || 0)
          const label = String(parsed.label || 'Detaylı bilgi için tıklayın')
          if (moduleId > 0) {
            nodes.push(
              <button
                key={`module-link-${nodes.length}`}
                type="button"
                className={styles.actionLink}
                onClick={() => setModuleInfoId(moduleId)}
              >
                <i className="fa-regular fa-circle-info" />
                {label}
              </button>
            )
          }
        } catch (_) {}
        return
      }

      textBuffer.push(line)
    })

    flushText()
    return nodes
  }

  useEffect(() => {
    const handleSearchHelp = (event: Event) => {
      const detail = (event as CustomEvent<{ query?: string }>).detail
      const query = String(detail?.query || '').trim()
      if (!query) return
      setOpen(true)
      setProactiveMsg(null)
      void sendMessage(query)
    }

    window.addEventListener('farktor:ai-search-help', handleSearchHelp as EventListener)
    return () => window.removeEventListener('farktor:ai-search-help', handleSearchHelp as EventListener)
  }, [sendMessage])

  if (isEmbedPath || !assistantEnabled) return null

  return (
    <div
      className={styles.root}
      style={{ transform: `translate(${pos.x}px, ${pos.y}px)` }}
    >
      {/* Proaktif balon */}
      {proactiveMsg && !open && (
        <div
          className={styles.proactiveBubble}
          onClick={() => {
            setOpen(true)
            setMessages([{ role: 'assistant', content: proactiveMsg, source: 'rule' }])
            setProactiveSeen(proactiveMsg)
            setProactiveMsg(null)
          }}
        >
          <p>{proactiveMsg}</p>
          <button
            className={styles.closeBubble}
            onClick={e => { e.stopPropagation(); setProactiveMsg(null) }}
          >×</button>
        </div>
      )}

      {/* Chat penceresi */}
      {open && (
        <div className={styles.window}>
          {/* Header — drag tutacağı */}
          <div
            className={styles.header}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
          >
            <div className={styles.headerLeft}>
              <span className={styles.headerIcon}>🤖</span>
              <div>
                <div className={styles.headerTitle}>Farktor Asistan</div>
                <div className={styles.headerSub}>{pageTitle}</div>
              </div>
            </div>
            <button className={styles.closeBtn} onClick={() => setOpen(false)}>×</button>
          </div>

          {/* Mesajlar */}
          <div className={styles.messages}>
            {messages.length === 0 && !streaming && (
              <div className={styles.emptyState}>
                <div className={styles.emptyIcon}>🤖</div>
                <p>Merhaba! <strong>{pageTitle}</strong> sayfasındasın.</p>
                <p>Sana bu sayfada yardımcı olabilirim.</p>
              </div>
            )}

            {messages.map((msg, i) => (
              <div key={i} className={`${styles.msg} ${styles[msg.role]}`}>
                {msg.role === 'assistant' ? renderMessageContent(msg.content) : msg.content}
              </div>
            ))}

            {streaming && (
              <div className={`${styles.msg} ${styles.assistant} ${styles.streaming}`}>
                {streaming}<span className={styles.cursor}/>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Hızlı butonlar */}
          {messages.length === 0 && !loading && (
            <div className={styles.quickBtns}>
              <button onClick={() => sendMessage('Bu sayfada ne yapabilirim?')}>
                Ne yapabilirim?
              </button>
              <button onClick={() => sendMessage('Nereden başlamalıyım?')}>
                Nasıl başlarım?
              </button>
            </div>
          )}

          {/* Input */}
          <div className={styles.inputRow}>
            <input
              className={styles.input}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage() } }}
              placeholder="Bir şey sor..."
              disabled={loading}
            />
            <button
              className={styles.sendBtn}
              onClick={() => sendMessage()}
              disabled={loading || !input.trim()}
            >
              {loading ? <span className={styles.loadingDot}/> : '↑'}
            </button>
          </div>
        </div>
      )}

      {/* Avatar */}
      <div
        className={`${styles.avatar} ${loading ? styles.avatarLoading : ''}`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onClick={handleAvatarClick}
        title="Farktor Asistan"
      >
        🤖
        {proactiveMsg && !open && <span className={styles.notifDot}/>}
      </div>

      <ModuleInfoModal
        open={moduleInfoId > 0}
        moduleId={moduleInfoId}
        onClose={() => setModuleInfoId(0)}
        source="ai_assistant_module_link"
        metadata={{ page: pathname }}
        eyebrow="Farktor Asistan"
      />
    </div>
  )
}
